
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Layout from '@kronoss/bricks/Layout/Layout';
import { Container } from '@mui/material';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import styles from '@kronoss/styles/pages/legal.module.scss';
const TermsAndConditionsPage = () => {
    const { t } = useTranslation('terms-and-conditions');
    const serviceItems: string[] = t('sections.serviceDescription.items', {}, { returnObjects: true });
    const registrationItems: string[] = t('sections.accountRegistration.items', {}, { returnObjects: true });
    const accountTypes: {
        name: string;
        description: string;
    }[] = t('sections.accountRegistration.accountTypes.types', {}, { returnObjects: true });
    const paymentMethodItems: string[] = t('sections.payments.paymentMethods.items', {}, { returnObjects: true });
    const refundItems: string[] = t('sections.payments.refunds.items', {}, { returnObjects: true });
    const cancellationItems: string[] = t('sections.lessonPolicies.cancellation.items', {}, { returnObjects: true });
    const conductItems: string[] = t('sections.userConduct.items', {}, { returnObjects: true });
    const teacherItems: string[] = t('sections.teacherTerms.items', {}, { returnObjects: true });
    const parentItems: string[] = t('sections.parentTerms.items', {}, { returnObjects: true });
    const liabilityItems: string[] = t('sections.limitationOfLiability.items', {}, { returnObjects: true });
    const terminationItems: string[] = t('sections.termination.items', {}, { returnObjects: true });
    return (<>
      <Head>
        <title>{t('metainfo:terms.title')}</title>
        <link rel="canonical" href="https://kronoss.education/terms-and-conditions"/>
        <link rel="alternate" hrefLang="en" href="https://kronoss.education/en/terms-and-conditions"/>
        <link rel="alternate" hrefLang="es" href="https://kronoss.education/es/terms-and-conditions"/>
        <meta name="description" content={t('metainfo:terms.description')}/>
        <meta property="og:title" content={t('metainfo:terms.slogan')}/>
        <meta property="og:description" content={t('metainfo:terms.description')}/>
        <meta name="twitter:title" content={t('metainfo:terms.slogan')}/>
        <meta name="twitter:description" content={t('metainfo:terms.description')}/>
      </Head>
      <Layout>
        <Container>
          <div className={styles.LegalPage}>
            <h1 className={styles.Title}>{t('title')}</h1>
            <p className={styles.LastUpdated}>{t('lastUpdated')}</p>
            <p className={styles.Intro}>{t('intro')}</p>

            {/* 1. Service Description */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.serviceDescription.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.serviceDescription.description')}
              </p>
              <ul className={styles.List}>
                {serviceItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 2. Account Registration */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.accountRegistration.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.accountRegistration.description')}
              </p>
              <ul className={styles.List}>
                {registrationItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.accountRegistration.accountTypes.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.accountRegistration.accountTypes.description')}
                </p>
                {accountTypes.map((type, i) => (<div key={i} className={styles.ServiceItem}>
                    <span className={styles.ServiceName}>
                      {type.name}
                    </span>
                    {' — '}
                    <span className={styles.ServicePurpose}>
                      {type.description}
                    </span>
                  </div>))}
              </div>

              <p className={styles.Notice}>
                {t('sections.accountRegistration.ageRequirement')}
              </p>
            </div>

            {/* 3. Payments */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.payments.title')}
              </h2>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.payments.pricing.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.payments.pricing.description')}
                </p>
              </div>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.payments.paymentMethods.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.payments.paymentMethods.description')}
                </p>
                <ul className={styles.List}>
                  {paymentMethodItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              </div>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.payments.packages.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.payments.packages.description')}
                </p>
              </div>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.payments.refunds.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.payments.refunds.description')}
                </p>
                <ul className={styles.List}>
                  {refundItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
                <p className={styles.Notice}>
                  {t('sections.payments.refunds.notice')}
                </p>
              </div>
            </div>

            {/* 4. Lesson Policies */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.lessonPolicies.title')}
              </h2>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.lessonPolicies.cancellation.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.lessonPolicies.cancellation.description')}
                </p>
                <ul className={styles.List}>
                  {cancellationItems.map((item, i) => (<li key={i}>{item}</li>))}
                </ul>
              </div>

              <div className={styles.SubSection}>
                <h3 className={styles.SubSectionTitle}>
                  {t('sections.lessonPolicies.attendance.title')}
                </h3>
                <p className={styles.Description}>
                  {t('sections.lessonPolicies.attendance.description')}
                </p>
              </div>
            </div>

            {/* 5. Acceptable Use */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.userConduct.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.userConduct.description')}
              </p>
              <ul className={styles.List}>
                {conductItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 6. Intellectual Property */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.intellectualProperty.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.intellectualProperty.description')}
              </p>
              <p className={styles.Description}>
                {t('sections.intellectualProperty.userContent')}
              </p>
              <p className={styles.Description}>
                {t('sections.intellectualProperty.restrictions')}
              </p>
            </div>

            {/* 7. Teacher Terms */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.teacherTerms.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.teacherTerms.description')}
              </p>
              <ul className={styles.List}>
                {teacherItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 8. Parent Terms */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.parentTerms.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.parentTerms.description')}
              </p>
              <ul className={styles.List}>
                {parentItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 9. Limitation of Liability */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.limitationOfLiability.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.limitationOfLiability.description')}
              </p>
              <ul className={styles.List}>
                {liabilityItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
            </div>

            {/* 10. Disclaimer */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.disclaimer.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.disclaimer.description')}
              </p>
            </div>

            {/* 11. Termination */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.termination.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.termination.description')}
              </p>
              <ul className={styles.List}>
                {terminationItems.map((item, i) => (<li key={i}>{item}</li>))}
              </ul>
              <p className={styles.Notice}>
                {t('sections.termination.notice')}
              </p>
            </div>

            {/* 12. Dispute Resolution */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.disputeResolution.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.disputeResolution.description')}
              </p>
            </div>

            {/* 13. Changes */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.changes.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.changes.description')}
              </p>
            </div>

            {/* 14. Contact */}
            <div className={styles.Section}>
              <h2 className={styles.SectionTitle}>
                {t('sections.contact.title')}
              </h2>
              <p className={styles.Description}>
                {t('sections.contact.description')}
              </p>
              <a href={`mailto:${t('sections.contact.email')}`} className={styles.EmailLink}>
                {t('sections.contact.email')}
              </a>
            </div>
          </div>
        </Container>
      </Layout>
    </>);
};
export default TermsAndConditionsPage;

    async function __Next_Translate__getStaticProps__19c38b8bf8c__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/terms-and-conditions',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__19c38b8bf8c__ as getStaticProps }
  